import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import { useTenant } from '../contexts/TenantContext';
import {
  getAdminJobBoards, createJobBoard, updateJobBoard, deleteJobBoard,
  getJobBoardAnalytics, seedAustralianJobBoards
} from '../api/endpoints';
import {
  Briefcase, Plus, Edit3, Trash2, Save, X, Loader2,
  ExternalLink, BarChart3, EyeOff, Star, Search,
  MousePointerClick, HardDrive
} from 'lucide-react';

// All supported countries
const COUNTRIES = [
  'Australia', 'New Zealand', 'United Kingdom', 'United States',
  'Canada', 'Ireland', 'Singapore', 'Malaysia', 'India', 'South Africa'
];

// Tenant options
const TENANT_OPTIONS = [
  { value: 'all', label: 'All Professions' },
  { value: 'med', label: 'Medicine' },
  { value: 'nursing', label: 'Nursing' },
  { value: 'pa', label: 'Physician Assistant' },
  { value: 'physio', label: 'Physiotherapy' },
  { value: 'dent', label: 'Dentistry' },
  { value: 'pharm', label: 'Pharmacy' },
];

const EMPTY_BOARD = {
  tenant_ids: ['all'],
  country: 'Australia',
  name: '',
  description: '',
  url: '',
  affiliate_url: '',
  logo_url: '',
  is_sponsored: false,
  sponsor_fee_monthly: 0,
  priority: 99,
};

const AdminJobBoardsPage = () => {
  const { tenant } = useTenant();
  const tenantName = tenant?.name || 'MedMCQ';

  // State
  const [boards, setBoards] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Filters
  const [filterCountry, setFilterCountry] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Edit/Create modal
  const [showModal, setShowModal] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_BOARD });

  // Analytics panel
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Seeding
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    document.title = `Job Boards Admin | ${tenantName}`;
  }, [tenantName]);

  const fetchBoards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filterCountry) params.country = filterCountry;
      const response = await getAdminJobBoards(params);
      setBoards(response.data.boards || []);
    } catch (err) {
      console.error('Error fetching boards:', err);
      setError('Failed to load job boards.');
    } finally {
      setLoading(false);
    }
  }, [filterCountry]);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const response = await getJobBoardAnalytics({ days: 30 });
      setAnalytics(response.data.analytics || []);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleSeedAustralian = async () => {
    setSeeding(true);
    setError(null);
    try {
      const response = await seedAustralianJobBoards();
      showSuccess(response.data.message || 'Australian job boards seeded successfully');
      fetchBoards();
    } catch (err) {
      console.error('Error seeding boards:', err);
      setError('Failed to seed Australian job boards.');
    } finally {
      setSeeding(false);
    }
  };

  // --- Handlers ---

  const handleCreate = () => {
    setEditingBoard(null);
    setFormData({ ...EMPTY_BOARD });
    setShowModal(true);
  };

  const handleEdit = (board) => {
    setEditingBoard(board);
    setFormData({
      tenant_ids: board.tenant_ids || ['all'],
      country: board.country,
      name: board.name,
      description: board.description,
      url: board.url,
      affiliate_url: board.affiliate_url || '',
      logo_url: board.logo_url || '',
      is_sponsored: board.is_sponsored || false,
      sponsor_fee_monthly: board.sponsor_fee_monthly || 0,
      priority: board.priority || 99,
    });
    setShowModal(true);
  };

  const handleDelete = async (boardId) => {
    try {
      await deleteJobBoard(boardId);
      showSuccess('Job board deactivated.');
      setConfirmDelete(null);
      fetchBoards();
    } catch (err) {
      console.error('Error deleting board:', err);
      setError('Failed to delete job board.');
    }
  };

  const handleSave = async () => {
    // Basic validation
    if (!formData.name.trim() || !formData.url.trim() || !formData.country) {
      setError('Name, URL, and Country are required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        affiliate_url: formData.affiliate_url || null,
        logo_url: formData.logo_url || null,
        sponsor_fee_monthly: parseFloat(formData.sponsor_fee_monthly) || 0,
        priority: parseInt(formData.priority) || 99,
      };

      if (editingBoard) {
        await updateJobBoard(editingBoard.id, payload);
        showSuccess('Job board updated.');
      } else {
        await createJobBoard(payload);
        showSuccess('Job board created.');
      }
      setShowModal(false);
      fetchBoards();
    } catch (err) {
      console.error('Error saving board:', err);
      setError(err.response?.data?.detail || 'Failed to save job board.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAnalytics = () => {
    if (!showAnalytics) {
      fetchAnalytics();
    }
    setShowAnalytics(!showAnalytics);
  };

  const handleTenantToggle = (tenantValue) => {
    setFormData(prev => {
      let newIds = [...prev.tenant_ids];
      if (tenantValue === 'all') {
        // Toggle "all" — if already has "all", remove it; otherwise set to just ["all"]
        return { ...prev, tenant_ids: newIds.includes('all') ? [] : ['all'] };
      }
      // Remove "all" if selecting a specific tenant
      newIds = newIds.filter(t => t !== 'all');
      if (newIds.includes(tenantValue)) {
        newIds = newIds.filter(t => t !== tenantValue);
      } else {
        newIds.push(tenantValue);
      }
      // If nothing selected, default to "all"
      if (newIds.length === 0) newIds = ['all'];
      return { ...prev, tenant_ids: newIds };
    });
  };

  // Filtered boards
  const filteredBoards = boards.filter(b => {
    if (searchTerm && !b.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !b.country.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  // Group by country for display
  const boardsByCountry = {};
  filteredBoards.forEach(b => {
    if (!boardsByCountry[b.country]) boardsByCountry[b.country] = [];
    boardsByCountry[b.country].push(b);
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Icon3D icon={Briefcase} colour="amber" size="md" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Job Boards Management</h1>
              <p className="text-slate-500">Manage country-specific job board links</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSeedAustralian}
              disabled={seeding}
              className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-xl font-medium text-sm disabled:opacity-60 transition-all"
              style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
              title="Seed default Australian job boards if none exist"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <HardDrive className="w-4 h-4" />}
              Seed AU Boards
            </button>
            <button
              onClick={handleToggleAnalytics}
              className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-xl font-medium text-sm transition-all"
              style={{ background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
            >
              <BarChart3 className="w-4 h-4" />
              {showAnalytics ? 'Hide' : 'Show'} Analytics
            </button>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-xl font-medium text-sm transition-all"
              style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
            >
              <Plus className="w-4 h-4" />
              Add Job Board
            </button>
          </div>
        </div>

        {/* Success / Error Messages */}
        {successMsg && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 text-green-700 text-sm font-medium">
            {successMsg}
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center justify-between">
            <span className="text-red-700 text-sm font-medium">{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <MousePointerClick className="w-5 h-5 text-cyan-600" />
              Click Analytics (Last 30 Days)
            </h3>
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-cyan-500 animate-spin" />
              </div>
            ) : analytics.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">No click data yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Job Board</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.map((item, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="py-2 px-3 text-slate-800">{item.board_name || item._id}</td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-cyan-700">{item.total_clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search boards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterCountry}
            onChange={(e) => setFilterCountry(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
          >
            <option value="">All Countries</option>
            {COUNTRIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <span className="text-sm text-slate-500">
            {filteredBoards.length} board{filteredBoards.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
          </div>
        )}

        {/* Boards by Country */}
        {!loading && Object.keys(boardsByCountry).sort().map(country => (
          <div key={country} className="mb-8">
            <h2 className="text-lg font-bold text-slate-800 mb-3 border-b border-slate-200 pb-2">
              {country} ({boardsByCountry[country].length})
            </h2>
            <div className="space-y-2">
              {boardsByCountry[country]
                .sort((a, b) => a.priority - b.priority)
                .map(board => (
                  <div
                    key={board.id}
                    className={`bg-white rounded-lg border p-4 flex items-center gap-4 ${
                      !board.is_active ? 'opacity-50 border-slate-200' :
                      board.is_sponsored ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'
                    }`}
                  >
                    {/* Priority */}
                    <div className="w-8 text-center">
                      <span className="text-xs font-mono text-slate-400">#{board.priority}</span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{board.name}</span>
                        {board.is_sponsored && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-semibold">
                            <Star className="w-3 h-3" /> Sponsored
                          </span>
                        )}
                        {!board.is_active && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">
                            <EyeOff className="w-3 h-3" /> Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 truncate">{board.description}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                        <span>Tenants: {Array.isArray(board.tenant_ids) ? board.tenant_ids.join(', ') : board.tenant_ids || 'N/A'}</span>
                        {board.affiliate_url && (
                          <span className="text-green-600 font-medium">Affiliate link</span>
                        )}
                        {board.is_sponsored && board.sponsor_fee_monthly > 0 && (
                          <span className="text-amber-600">${board.sponsor_fee_monthly}/mo</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <a
                        href={board.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                        title="Open URL"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleEdit(board)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      {confirmDelete === board.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(board.id)}
                            className="px-2 py-1 bg-red-500 text-white text-xs rounded font-medium hover:bg-red-600"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="px-2 py-1 bg-slate-200 text-slate-600 text-xs rounded font-medium hover:bg-slate-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(board.id)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Deactivate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}

        {/* Empty */}
        {!loading && filteredBoards.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            <Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No job boards found</p>
            <p className="text-sm mt-1 mb-4">{filterCountry || searchTerm ? 'Adjust your filters.' : 'Seed the default Australian boards or create one manually.'}</p>
            {!filterCountry && !searchTerm && (
              <button
                onClick={handleSeedAustralian}
                disabled={seeding}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-white rounded-xl font-medium text-sm disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)' }}
              >
                {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <HardDrive className="w-4 h-4" />}
                Seed Australian Job Boards
              </button>
            )}
          </div>
        )}

        {/* Create / Edit Modal */}
        {showModal && (
          <>
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowModal(false)} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="flex items-center justify-between p-5 border-b border-slate-200">
                  <h2 className="text-lg font-bold text-slate-900">
                    {editingBoard ? 'Edit Job Board' : 'Add Job Board'}
                  </h2>
                  <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-5 space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. Seek Healthcare"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
                    <select
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
                    >
                      {COUNTRIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of the job board"
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>

                  {/* URL */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">URL *</label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://www.example.com"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>

                  {/* Affiliate URL */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Affiliate URL
                      <span className="text-slate-400 font-normal ml-1">(optional, used for monetisation)</span>
                    </label>
                    <input
                      type="url"
                      value={formData.affiliate_url}
                      onChange={(e) => setFormData({ ...formData, affiliate_url: e.target.value })}
                      placeholder="https://www.example.com?ref=medmcq"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>

                  {/* Logo URL */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Logo URL
                      <span className="text-slate-400 font-normal ml-1">(optional)</span>
                    </label>
                    <input
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://www.example.com/logo.png"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>

                  {/* Professions (tenant_ids) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Professions</label>
                    <div className="flex flex-wrap gap-2">
                      {TENANT_OPTIONS.map(opt => {
                        const isSelected = formData.tenant_ids.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleTenantToggle(opt.value)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                              isSelected
                                ? 'bg-cyan-50 border-cyan-300 text-cyan-700'
                                : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                      <input
                        type="number"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        min={1}
                        max={999}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                      <p className="text-xs text-slate-400 mt-1">Lower = shown first</p>
                    </div>

                    {/* Sponsor Fee */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Sponsor Fee ($/mo)</label>
                      <input
                        type="number"
                        value={formData.sponsor_fee_monthly}
                        onChange={(e) => setFormData({ ...formData, sponsor_fee_monthly: e.target.value })}
                        min={0}
                        step={0.01}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>

                  {/* Sponsored toggle */}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_sponsored: !formData.is_sponsored })}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        formData.is_sponsored ? 'bg-amber-500' : 'bg-slate-300'
                      }`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        formData.is_sponsored ? 'translate-x-5' : ''
                      }`} />
                    </button>
                    <span className="text-sm text-slate-700 font-medium">Sponsored placement</span>
                    {formData.is_sponsored && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-semibold">
                        <Star className="w-3 h-3" /> Featured
                      </span>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-200">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 font-medium text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)' }}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {editingBoard ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminJobBoardsPage;
